import { useEffect, useId, useState } from "react";
import { api } from "../api";
import { Badge, Spinner } from "./Shared";

const ROLE_COLOR = { PI: "green", Owner: "blue", Operator: "amber", Member: "gray" };

// Names the people a delivery notice can go to, one checkbox each. The list is
// closed on purpose — the backend validates every address against the same set
// (see _resolve_recipients), so there is deliberately no "type an address" box.
export default function RecipientPicker({ projectId, selected, onChange, disabled = false }) {
  const [candidates, setCandidates] = useState(null);
  const [error, setError] = useState(null);
  const groupId = useId();

  useEffect(() => {
    let cancelled = false;
    api.get(`/api/projects/${projectId}/notification-recipients`)
      .then(rows => { if (!cancelled) { setCandidates(rows); setError(null); } })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [projectId]);

  if (error) {
    return (
      <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
        Could not load recipients: {error}
      </div>
    );
  }
  if (candidates === null) {
    return <div style={{ fontSize: 13, color: "#6b7280", padding: "10px 0" }}><Spinner /> Loading recipients…</div>;
  }

  const reachable = candidates.filter(c => c.email);
  const isOn = email => selected.some(s => s.toLowerCase() === email.toLowerCase());
  const toggle = email => {
    onChange(isOn(email)
      ? selected.filter(s => s.toLowerCase() !== email.toLowerCase())
      : [...selected, email]);
  };

  if (candidates.length === 0) {
    return (
      <div style={{ fontSize: 13, color: "#6b7280", padding: "10px 0" }}>
        Nobody is attached to this project yet — add a PI email address or project members first.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 12 }}>
        <span style={{ fontSize: 12, color: "#4b5563" }}>
          {selected.length} of {reachable.length} selected
        </span>
        {reachable.length > 1 && (
          <div style={{ display: "flex", gap: 12 }}>
            <button type="button" disabled={disabled}
              onClick={() => onChange(reachable.map(c => c.email))}
              style={linkBtn}>Select all</button>
            <button type="button" disabled={disabled}
              onClick={() => onChange([])}
              style={linkBtn}>Select none</button>
          </div>
        )}
      </div>

      <ul style={{ listStyle: "none", margin: 0, padding: 0, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
        {candidates.map((c, i) => {
          const id = `${groupId}-r${i}`;
          const off = disabled || !c.email;
          return (
            <li key={id} style={{ borderTop: i ? "1px solid #f3f4f6" : "none", background: c.email ? "#fff" : "#f9fafb" }}>
              <label htmlFor={id} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                cursor: off ? "default" : "pointer", minHeight: 24,
              }}>
                <input
                  id={id}
                  type="checkbox"
                  disabled={off}
                  checked={!!c.email && isOn(c.email)}
                  onChange={() => c.email && toggle(c.email)}
                  style={{ width: 16, height: 16, flexShrink: 0, cursor: off ? "default" : "pointer" }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, color: c.email ? "#1a1a1a" : "#6b7280" }}>{c.name}</span>
                    {c.roles.map(role => <Badge key={role} color={ROLE_COLOR[role] || "gray"}>{role}</Badge>)}
                  </span>
                  <span style={{
                    display: "block", fontSize: 11, marginTop: 2,
                    fontFamily: c.email ? "'JetBrains Mono', monospace" : "inherit",
                    color: "#6b7280",
                  }}>
                    {/* Someone with no address is shown anyway, with the reason —
                        otherwise "why can't I pick X?" has no answer on screen. */}
                    {c.email || c.reason}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const linkBtn = {
  background: "none", border: "none", padding: 0, fontFamily: "inherit",
  fontSize: 12, color: "#005587", cursor: "pointer", textDecoration: "underline",
};
